import { Menu, Button, Checkbox, Stack, Group, Divider } from '@mantine/core';
import { IconColumns, IconEye, IconEyeOff } from '@tabler/icons-react';
import type { ColumnDefinition } from '@/hooks/useColumnVisibility';

interface ColumnVisibilityMenuProps<T extends string = string> {
  columns: ColumnDefinition<T>[];
  visibleColumns: Set<T>;
  onToggleColumn: (key: T) => void;
  onShowAll: () => void;
  onResetDefaults: () => void;
}

export function ColumnVisibilityMenu<T extends string = string>({
  columns,
  visibleColumns,
  onToggleColumn,
  onShowAll,
  onResetDefaults,
}: ColumnVisibilityMenuProps<T>) {
  const allVisible = columns.every((col) => visibleColumns.has(col.key));
  const someVisible = columns.some((col) => visibleColumns.has(col.key));

  return (
    <Menu shadow="md" width={250} position="bottom-end">
      <Menu.Target>
        <Button variant="default" leftSection={<IconColumns size={16} />}>
          Columns
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Show/Hide Columns</Menu.Label>

        <Stack gap="xs" p="xs">
          {columns.map((column) => (
            <Checkbox
              key={column.key}
              label={column.label}
              checked={visibleColumns.has(column.key)}
              onChange={() => onToggleColumn(column.key)}
            />
          ))}
        </Stack>

        <Divider my="xs" />

        <Group gap="xs" p="xs">
          <Button
            variant="light"
            size="xs"
            leftSection={<IconEye size={14} />}
            onClick={onShowAll}
            disabled={allVisible}
            fullWidth
          >
            Show All
          </Button>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconEyeOff size={14} />}
            onClick={onResetDefaults}
            disabled={!someVisible}
            fullWidth
          >
            Reset
          </Button>
        </Group>
      </Menu.Dropdown>
    </Menu>
  );
}
